import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Sanitizes a navigation target before it is assigned to `window.location.href`,
 * guarding against open-redirect and XSS (e.g. `javascript:` URLs). Only
 * same-origin, root-relative paths are allowed — a single leading slash, no
 * protocol-relative `//host` form, and no scheme. Anything else collapses to
 * the app root (`/`).
 */
export function safeInternalHref(to: string): string {
  if (typeof to !== "string" || to.length === 0) return "/";
  // Must be root-relative; reject protocol-relative `//evil.com`.
  if (!to.startsWith("/") || to.startsWith("//")) return "/";
  // Reject any control characters (e.g. embedded newlines used to smuggle a
  // `javascript:` payload past a naive check).
  for (let i = 0; i < to.length; i++) {
    if (to.charCodeAt(i) < 0x20) return "/";
  }
  // Reject any scheme-like prefix (`javascript:`, `data:`, etc.).
  if (/^\s*[a-z][a-z0-9+.-]*:/i.test(to)) return "/";
  return to;
}

/**
 * True when `url` is a safe target for a full-page external redirect
 * (`window.location.href = url`). Only absolute `http:`/`https:` URLs pass —
 * `javascript:`, `data:`, `vbscript:`, `blob:`, and unparseable input are
 * rejected. Cross-origin is intentionally allowed: this guards redirects whose
 * destination may legitimately be another origin (e.g. an OAuth `redirect_uri`).
 */
export function isSafeRedirectUrl(url: unknown): url is string {
  if (typeof url !== "string" || url.length === 0) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

/**
 * True when `href` resolves to the **current document's** origin and protocol —
 * a safe target for internal `window.open`. `href` may be app-relative (`/path`)
 * or a hash route (`#/path`, used by the desktop app's hash history); both
 * resolve against `window.location` and match. A scheme mismatch (e.g.
 * `javascript:`) or a different origin is rejected. Returns `false` when there
 * is no `window` (SSR/non-browser).
 */
export function isSameOriginHref(href: unknown): boolean {
  if (typeof href !== "string" || href.length === 0) return false;
  if (typeof window === "undefined") return false;
  const base = window.location.href;
  let resolved: URL;
  let current: URL;
  try {
    resolved = new URL(href, base);
    current = new URL(base);
  } catch {
    return false;
  }
  return resolved.protocol === current.protocol && resolved.origin === current.origin;
}
